/**
 * Shared login action for `mutagent login` and `mutagent auth login`.
 *
 * Both commands are thin wrappers around `performLoginAction()`. The browser
 * OAuth flow (`performBrowserAuth` from lib/browser-auth.ts) is called from
 * exactly one place in this module — single source of truth for the canonical
 * signup → onboard → authorize CLI session sequence.
 *
 * Decision: .memory/design-principles.md → "Login Unification (Decision Record)"
 * Paper trail: .memory/key-learnings/login-unification.md
 */

import inquirer from 'inquirer';
import chalk from 'chalk';
import ora from 'ora';
import { saveFullCredentials, hasCredentials } from './config.js';
import { validateApiKey, fetchOrganizations, fetchWorkspaces } from './sdk-client.js';
import type { OutputFormatter } from './output.js';
import { MutagentError } from './errors.js';
import { performBrowserAuth, BrowserAuthError } from './browser-auth.js';

export interface LoginActionOptions {
  endpoint: string;
  browser?: boolean;
  nonInteractive?: boolean;
  isJson: boolean;
  output: OutputFormatter;
}

export interface LoginActionResult {
  authenticated: boolean;
  apiKey: string;
  endpoint: string;
  workspace: { id: string; name?: string } | null;
  organization: { id: string; name?: string } | null;
  wasFirstLogin: boolean;
}

interface AuthMethodAnswer {
  method: 'browser' | 'apiKey';
}

interface LoginAnswers {
  apiKey: string;
  endpoint: string;
}

/**
 * Shared login entrypoint. Resolution order:
 *   1. MUTAGENT_API_KEY env var → fast path
 *   2. --browser flag → browser OAuth
 *   3. Non-interactive context → auto-browser
 *   4. Interactive TTY → method choice prompt
 */
export async function performLoginAction(opts: LoginActionOptions): Promise<LoginActionResult> {
  const { isJson, output } = opts;
  const wasFirstLogin = !hasCredentials();
  const envApiKey: string | undefined = process.env.MUTAGENT_API_KEY;
  const endpoint: string = process.env.MUTAGENT_ENDPOINT ?? opts.endpoint;

  // 1. Env var fast path
  if (envApiKey) {
    return loginWithExistingKey(envApiKey, endpoint, output, wasFirstLogin);
  }

  // 2. Detect non-interactive environment
  const isNonInteractive =
    opts.nonInteractive === true ||
    process.env.MUTAGENT_NON_INTERACTIVE === 'true' ||
    process.env.CI === 'true' ||
    !process.stdin.isTTY;

  // 3. JSON + non-interactive without browser flag cannot proceed
  if (isJson && !opts.browser && isNonInteractive) {
    throw new MutagentError(
      'INTERACTIVE_REQUIRED',
      'No API key provided. Set MUTAGENT_API_KEY env var or add --browser for browser auth.',
      'Run: export MUTAGENT_API_KEY=<key> or mutagent login --browser --non-interactive'
    );
  }

  // 4. Welcome banner for first-time interactive users
  if (!isNonInteractive && wasFirstLogin) {
    console.log('\n' + chalk.bold.cyan('  Welcome to MutagenT CLI!') + '\n');
    console.log('  No credentials found. Please authenticate to continue.\n');
  }

  // 5. Determine method
  let useBrowserAuth = opts.browser === true;

  if (!useBrowserAuth && isNonInteractive) {
    output.info('Non-interactive environment detected. Using browser authentication.');
    useBrowserAuth = true;
  }

  if (!useBrowserAuth) {
    const methodAnswer = await inquirer.prompt<AuthMethodAnswer>([
      {
        type: 'list',
        name: 'method',
        message: 'How would you like to authenticate?',
        choices: [
          { name: 'Login (opens browser)', value: 'browser' },
          { name: 'API Key (paste existing key)', value: 'apiKey' },
        ],
      },
    ]);
    useBrowserAuth = methodAnswer.method === 'browser';
  }

  // 6. Execute chosen path
  if (useBrowserAuth) {
    return loginWithBrowser(endpoint, output, wasFirstLogin);
  }
  return loginWithPastedKey(endpoint, output, wasFirstLogin);
}

/**
 * Build the canonical JSON response emitted by both `mutagent login --json`
 * and `mutagent auth login --json`. Shape MUST remain byte-identical to the
 * pre-unification `auth.ts` JSON for back-compat with existing agent parsers.
 */
export function buildLoginJsonResponse(result: LoginActionResult): Record<string, unknown> {
  return {
    success: true,
    authenticated: result.authenticated,
    endpoint: result.endpoint,
    workspace: result.workspace,
    organization: result.organization,
    _directive: {
      instruction: 'Verify workspace. Run: mutagent workspaces list --json',
      next: ['mutagent workspaces list --json', 'mutagent prompts list --json'],
    },
  };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

async function loginWithExistingKey(
  apiKey: string,
  endpoint: string,
  output: OutputFormatter,
  wasFirstLogin: boolean
): Promise<LoginActionResult> {
  output.info('Validating API key...');
  const isValid = await validateApiKey(apiKey, endpoint);
  if (!isValid) {
    throw new MutagentError(
      'INVALID_API_KEY',
      'Invalid API key or endpoint',
      'Check your API key and try again'
    );
  }

  const orgs = await fetchOrganizations(apiKey, endpoint);
  let orgId: string | undefined;
  let orgName: string | undefined;
  let wsId: string | undefined;
  let wsName: string | undefined;

  if (orgs.length >= 1 && orgs[0]) {
    orgId = orgs[0].id;
    orgName = orgs[0].name;
    const workspaces = await fetchWorkspaces(apiKey, endpoint, orgId);
    const defaultWs = workspaces.find(w => w.isDefault) ?? workspaces[0];
    if (defaultWs) {
      wsId = defaultWs.id;
      wsName = defaultWs.name;
    }
  }

  saveFullCredentials({
    apiKey,
    endpoint,
    workspaceId: wsId,
    organizationId: orgId,
  });

  return {
    authenticated: true,
    apiKey,
    endpoint,
    workspace: wsId ? { id: wsId, name: wsName } : null,
    organization: orgId ? { id: orgId, name: orgName } : null,
    wasFirstLogin,
  };
}

async function loginWithBrowser(
  endpoint: string,
  output: OutputFormatter,
  wasFirstLogin: boolean
): Promise<LoginActionResult> {
  // NOTE: This is the ONLY call site of performBrowserAuth in the CLI.
  const spinner = ora({ text: 'Opening browser for authentication...', spinner: 'dots' });
  try {
    spinner.start();
    const result = await performBrowserAuth(
      { endpoint, timeout: 300000, pollInterval: 2000 },
      (message) => {
        spinner.text = message;
      }
    );
    spinner.succeed('Authenticated successfully!');

    saveFullCredentials({
      apiKey: result.apiKey,
      endpoint,
      workspaceId: result.workspaceId,
      organizationId: result.organizationId,
      expiresAt: result.expiresAt,
    });

    if (result.workspaceName) output.info(`Workspace: ${result.workspaceName}`);
    if (result.organizationName) output.info(`Organization: ${result.organizationName}`);

    return {
      authenticated: true,
      apiKey: result.apiKey,
      endpoint,
      workspace: result.workspaceId
        ? { id: result.workspaceId, name: result.workspaceName }
        : null,
      organization: result.organizationId
        ? { id: result.organizationId, name: result.organizationName }
        : null,
      wasFirstLogin,
    };
  } catch (error) {
    spinner.fail('Authentication failed');
    if (error instanceof BrowserAuthError) {
      throw new MutagentError(error.code, error.message, error.getSuggestion());
    }
    throw error;
  }
}

async function loginWithPastedKey(
  initialEndpoint: string,
  output: OutputFormatter,
  wasFirstLogin: boolean
): Promise<LoginActionResult> {
  const answers = await inquirer.prompt<LoginAnswers>([
    {
      type: 'input',
      name: 'endpoint',
      message: 'MutagenT endpoint:',
      default: initialEndpoint,
    },
    {
      type: 'password',
      name: 'apiKey',
      message: 'API Key:',
      mask: '*',
      validate: (input: string): boolean | string => input.length > 0 || 'API key is required',
    },
  ]);

  const apiKey = answers.apiKey;
  const endpoint = answers.endpoint;

  output.info('Validating API key...');
  const isValid = await validateApiKey(apiKey, endpoint);
  if (!isValid) {
    throw new MutagentError(
      'INVALID_API_KEY',
      'Invalid API key or endpoint',
      'Check your API key and try again'
    );
  }

  // Interactive multi-tenant picker
  let selectedOrgId: string | undefined;
  let selectedOrgName: string | undefined;
  let selectedWsId: string | undefined;
  let selectedWsName: string | undefined;

  const orgs = await fetchOrganizations(apiKey, endpoint);
  if (orgs.length === 1 && orgs[0]) {
    selectedOrgId = orgs[0].id;
    selectedOrgName = orgs[0].name;
  } else if (orgs.length > 1) {
    const orgAnswer = await inquirer.prompt<{ orgId: string }>([
      {
        type: 'list',
        name: 'orgId',
        message: 'Select organization:',
        choices: orgs.map(o => ({ name: o.name, value: o.id })),
      },
    ]);
    selectedOrgId = orgAnswer.orgId;
    selectedOrgName = orgs.find(o => o.id === selectedOrgId)?.name;
  }

  if (selectedOrgId) {
    const workspaces = await fetchWorkspaces(apiKey, endpoint, selectedOrgId);
    const defaultWs = workspaces.find(w => w.isDefault);
    if (workspaces.length === 1 && workspaces[0]) {
      selectedWsId = workspaces[0].id;
      selectedWsName = workspaces[0].name;
    } else if (defaultWs) {
      selectedWsId = defaultWs.id;
      selectedWsName = defaultWs.name;
    } else if (workspaces.length > 1) {
      const wsAnswer = await inquirer.prompt<{ wsId: string }>([
        {
          type: 'list',
          name: 'wsId',
          message: 'Select workspace:',
          choices: workspaces.map(w => ({
            name: w.name + (w.isDefault ? ' (default)' : ''),
            value: w.id,
          })),
        },
      ]);
      selectedWsId = wsAnswer.wsId;
      selectedWsName = workspaces.find(w => w.id === selectedWsId)?.name;
    }
  }

  saveFullCredentials({
    apiKey,
    endpoint,
    workspaceId: selectedWsId,
    organizationId: selectedOrgId,
  });

  return {
    authenticated: true,
    apiKey,
    endpoint,
    workspace: selectedWsId ? { id: selectedWsId, name: selectedWsName } : null,
    organization: selectedOrgId ? { id: selectedOrgId, name: selectedOrgName } : null,
    wasFirstLogin,
  };
}
